import { ReactNode } from 'react';
import { LuCopy, LuPlus } from 'react-icons/lu';

import { Allocation, AllocationType, GUID, ManagementType } from '@ndvr/portfolio-api';

import { Combobox } from '@/components/ui/combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export type AllocationTabFilter = AllocationType | 'INFERRED';

interface AccountAllocationEditorProps {
    managementType: ManagementType;
    allocationGUID?: GUID<Allocation>;
    onManagementTypeChange: (managementType: ManagementType) => void;
    onAllocationGUIDChange: (allocationGUID: GUID<Allocation> | undefined) => void;
    allocationTypeFilter: AllocationTabFilter;
    onAllocationTypeFilterChange: (type: AllocationTabFilter) => void;
    filteredAllocations: Allocation[];
    isLoadingAllocations?: boolean;
    isLoadingPermissions?: boolean;
    /** When the account's current allocation is inferred, pass it here to show a dedicated tab. */
    inferredAllocation?: Allocation;
    /** Optional: when provided, renders a "+" button next to the allocation combobox. */
    onCreateAllocation?: () => void;
    /** Optional: when provided, renders a "clone" button next to the allocation combobox. */
    onCloneAllocation?: () => void;
    /** Optional: rendered right after the Management Type select, e.g. account-level flags. */
    afterManagementType?: ReactNode;
    className?: string;
}

export const AccountAllocationEditor = ({
    managementType,
    allocationGUID,
    onManagementTypeChange,
    onAllocationGUIDChange,
    allocationTypeFilter,
    onAllocationTypeFilterChange,
    filteredAllocations,
    isLoadingAllocations,
    isLoadingPermissions,
    inferredAllocation,
    onCreateAllocation,
    onCloneAllocation,
    afterManagementType,
    className,
}: AccountAllocationEditorProps) => {
    const isInferredTab = allocationTypeFilter === 'INFERRED';
    const comboboxOptions = isInferredTab
        ? inferredAllocation
            ? [{ value: inferredAllocation.allocationGUID, label: inferredAllocation.allocationName }]
            : []
        : filteredAllocations.map((a) => ({ value: a.allocationGUID, label: a.allocationName }));

    return (
        <div className={className ?? 'space-y-3'}>
            <div className="space-y-2">
                <label className="text-sm font-medium">Management Type</label>
                <Select
                    value={managementType}
                    onValueChange={(value) => onManagementTypeChange(value as ManagementType)}
                >
                    <SelectTrigger className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ManagementType.AUTOMATIC}>Automatic</SelectItem>
                        <SelectItem value={ManagementType.MANUAL}>Manual</SelectItem>
                        <SelectItem value={ManagementType.EXTERNAL}>External</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            {afterManagementType}
            {managementType === ManagementType.AUTOMATIC && (
                <div className="space-y-3">
                    <Tabs
                        value={allocationTypeFilter}
                        onValueChange={(value) => onAllocationTypeFilterChange(value as AllocationTabFilter)}
                    >
                        <TabsList className="border-none">
                            <TabsTrigger value={AllocationType.SHARED}>Shared</TabsTrigger>
                            <TabsTrigger value={AllocationType.PERSONAL}>My Allocations</TabsTrigger>
                            {inferredAllocation && <TabsTrigger value="INFERRED">Inferred</TabsTrigger>}
                        </TabsList>
                    </Tabs>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Select Allocation</label>
                        <div className="flex gap-2">
                            <Combobox
                                options={comboboxOptions}
                                value={allocationGUID}
                                onValueChange={(value) => onAllocationGUIDChange(value || undefined)}
                                placeholder={isLoadingAllocations ? 'Loading allocations...' : 'Select an allocation'}
                                disabled={isLoadingAllocations || isInferredTab}
                                className="min-w-0 flex-1"
                            />
                            {!isInferredTab && onCreateAllocation && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                onClick={onCreateAllocation}
                                                disabled={isLoadingAllocations}
                                                className="shrink-0 rounded border border-gray-300 p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <LuPlus size={20} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>Create new allocation</TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                            {!isInferredTab && onCloneAllocation && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                onClick={onCloneAllocation}
                                                disabled={
                                                    !allocationGUID || isLoadingAllocations || isLoadingPermissions
                                                }
                                                className="shrink-0 rounded border border-gray-300 p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <LuCopy size={20} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {isLoadingPermissions
                                                ? 'Loading permissions…'
                                                : allocationGUID
                                                  ? 'Clone current allocation'
                                                  : 'Select an allocation to clone'}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
